import { useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";

interface Props<T> {
  data: T[];
  columns: ColumnDef<T, any>[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  stickyFirstColumn?: boolean;
  onRowClick?: (row: T) => void;
  maxHeight?: string;
  initialSort?: SortingState;
  emptyMessage?: string;
}

export default function DataTable<T>({
  data,
  columns,
  stickyFirstColumn = false,
  onRowClick,
  maxHeight = "32rem",
  initialSort = [],
  emptyMessage = "No rows",
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSort);
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const firstStickyCls = stickyFirstColumn
    ? "first:sticky first:left-0 first:z-10 first:bg-inherit"
    : "";

  return (
    <div className="overflow-auto rounded-lg border border-ink-200" style={{ maxHeight }}>
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-20 bg-ink-50">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => {
                const sortable = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                const isNum = (header.column.columnDef.meta as { numeric?: boolean })?.numeric;
                return (
                  <th
                    key={header.id}
                    className={`whitespace-nowrap border-b border-ink-200 bg-ink-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-500 ${
                      isNum ? "text-right" : "text-left"
                    } ${stickyFirstColumn ? "first:sticky first:left-0 first:z-30" : ""}`}
                  >
                    {header.isPlaceholder ? null : sortable ? (
                      <button
                        onClick={header.column.getToggleSortingHandler()}
                        className={`inline-flex items-center gap-1 hover:text-ink-800 focus-ring ${isNum ? "flex-row-reverse" : ""}`}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <span className="text-ink-400">
                          {sorted === "asc" ? "▲" : sorted === "desc" ? "▼" : "↕"}
                        </span>
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-8 text-center text-sm text-ink-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                className={`group bg-white even:bg-ink-50/40 ${
                  onRowClick ? "cursor-pointer hover:bg-accent-50" : ""
                }`}
              >
                {row.getVisibleCells().map((cell) => {
                  const isNum = (cell.column.columnDef.meta as { numeric?: boolean })?.numeric;
                  return (
                    <td
                      key={cell.id}
                      className={`whitespace-nowrap border-b border-ink-100 px-3 py-2 text-ink-800 ${
                        isNum ? "text-right tabular-nums" : "text-left"
                      } ${stickyFirstColumn ? "first:sticky first:left-0 first:z-10 first:bg-white first:group-even:bg-ink-50 first:group-hover:bg-accent-50 first:font-medium" : ""} ${firstStickyCls}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
